import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";

import { useClaimsSchema } from "shared/schemas/claimsSchema";
import { EventTypeEnum } from "shared/types";

import {
  CLAIMS_TABLE_DEFAULT_SORT,
  CLAIMS_TABLE_PAGE_KEY,
} from "pages/ClaimAnalytics/constants";
import { MainTableComponentProps } from "pages/Dashboard/types";
import {
  getDashboardInvestigatorSearch,
  saveDashboardStateToLocalStorage,
} from "pages/Dashboard/utils";
import { useJsonState } from "pages/shared/Dashboard/useJsonState";
import { SIGNAL_EVENTS_TABLE_PAGE_KEY } from "pages/SignalEventsAnalytics/constants";

import Button from "features/components/Button";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import {
  getFiltersQuery,
  mergeFilterGroupStates,
} from "features/ui/Filters/FilterBuilder/utils";
import { useFilterSortState } from "features/ui/Filters/hooks";

import { routes } from "services/routes";

import { DEFAULT_CLAIMS_FIELDS, DEFAULT_SIGNAL_EVENTS_SORT } from "./constants";
import EventsSmartGroups from "./EventsSmartGroups";
import ClaimsTable from "./EventTables/ClaimsTable";
import SignalEventsTable from "./EventTables/SignalEventsTable";
import useSignalEventOccurrencesViewSchema from "./signalEventsOccurenceSchema";

interface EventsProps extends MainTableComponentProps {
  eventType: EventTypeEnum;
}

const saveFiltersToLocalStorage = (
  eventFilters: FilterGroupState,
  tableEventFilters: FilterGroupState,
  vehiclesFilters: FilterGroupState,
  eventType: EventTypeEnum
) => {
  const mergedEventFilters = mergeFilterGroupStates(
    eventFilters,
    tableEventFilters
  );

  saveDashboardStateToLocalStorage(
    getFiltersQuery(mergedEventFilters),
    getFiltersQuery(vehiclesFilters),
    eventType
  );
};

const Events = ({
  eventFilters,
  vehiclesFilters,
  allTimeVehiclesFilters,
  onBadRequest,
  config,
  dashboardName,
  dateField,
  eventType,
  vin,
  dateFieldOption,
  advancedFiltersDatePeriod,
  stickyHeader,
  stickyFooter,
}: EventsProps) => {
  const { t } = useTranslation();
  const smartGroupsAllowed = config.enableSmartGroups ?? false;
  const navigate = useNavigate();

  const filterSuffix = vin ? `${vin}` : "dashboard";

  // we use useJsonState to store it in the URL as well
  const { json: smartGroups, setJson: setSmartGroups } = useJsonState<boolean>({
    initialState: false,
    pageKey: `smart_groups_${dashboardName}_${filterSuffix}`,
  });

  const { attributeAccessors: claimsAttributeAccessors } = useClaimsSchema(
    [],
    config.columnAccessors || DEFAULT_CLAIMS_FIELDS
  );
  const { attributeAccessors: signalEventsAttributeAccessors } =
    useSignalEventOccurrencesViewSchema(config.columnAccessors);

  const schemaAttributes =
    eventType === EventTypeEnum.CLAIM
      ? claimsAttributeAccessors
      : signalEventsAttributeAccessors;

  const defaultSort =
    eventType === EventTypeEnum.CLAIM
      ? CLAIMS_TABLE_DEFAULT_SORT
      : DEFAULT_SIGNAL_EVENTS_SORT;

  // shared filter table state that persists across smart groups toggle
  const sharedTableFilterState = useFilterSortState({
    pageKey: `${eventType}_table_filter_${dashboardName}_${filterSuffix}`,
    defaultSort,
    schemaAttributes,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSmartGroups(event.target.checked);
  };

  const onNavigateToInvestigate = () => {
    const mergedEventFilters = mergeFilterGroupStates(
      eventFilters,
      sharedTableFilterState?.filters
    );

    navigate({
      pathname: generatePath(routes.investigator),
      search: getDashboardInvestigatorSearch({
        eventFilters: mergedEventFilters,
        vehiclesFilters,
        eventType,
        viewTitle: config.title,
      }),
    });
  };

  // Save merged filters to local storage when they change.
  // This is used to provide filters to duck app_state.
  useEffect(() => {
    saveFiltersToLocalStorage(
      eventFilters,
      sharedTableFilterState.filters,
      vehiclesFilters,
      eventType
    );
  }, [
    eventFilters,
    eventType,
    sharedTableFilterState.filters,
    vehiclesFilters,
  ]);

  const customActions = (
    <Button
      color="primary"
      size="small"
      onClick={onNavigateToInvestigate}
      testId="dashboard-page-investigate-button"
    >
      {t("schema:dashboard.investigate")}
    </Button>
  );

  const getTableComponent = () => {
    // both SE and Claims share the same component
    if (smartGroups && smartGroupsAllowed) {
      return (
        <EventsSmartGroups
          {...{
            eventFilters,
            vehiclesFilters,
            allTimeVehiclesFilters,
            onBadRequest,
            config,
            dashboardName,
            dateField,
            eventType,
            smartGroups,
            onSmartGroupsChange: handleChange,
            smartGroupsAllowed,
            sharedTableFilterState,
            dateFieldOption,
            advancedFiltersDatePeriod,
            stickyHeader,
            stickyFooter,
          }}
        />
      );
    }

    switch (eventType) {
      case EventTypeEnum.CLAIM:
        return (
          <ClaimsTable
            eventFilters={eventFilters}
            vehiclesFilters={vehiclesFilters}
            onBadRequest={onBadRequest}
            config={config}
            smartGroups={smartGroups}
            onSmartGroupsChange={handleChange}
            dashboardName={dashboardName}
            downloadSource="smart-groups"
            pageKey={`${CLAIMS_TABLE_PAGE_KEY}_${dashboardName}_${filterSuffix}`}
            sharedTableFilterState={sharedTableFilterState}
            customActions={customActions}
            rowTestId="smart-group-action-click"
            stickyHeader={stickyHeader}
            stickyFooter={stickyFooter}
          />
        );
      case EventTypeEnum.SIGNAL_EVENT:
        return (
          <SignalEventsTable
            eventFilters={eventFilters}
            vehiclesFilters={vehiclesFilters}
            onBadRequest={onBadRequest}
            config={config}
            smartGroups={smartGroups}
            onSmartGroupsChange={handleChange}
            dashboardName={dashboardName}
            downloadSource="smart-groups"
            pageKey={`${SIGNAL_EVENTS_TABLE_PAGE_KEY}_${dashboardName}_${filterSuffix}`}
            sharedTableFilterState={sharedTableFilterState}
            customActions={customActions}
            rowTestId="smart-group-action-click"
            stickyHeader={stickyHeader}
            stickyFooter={stickyFooter}
          />
        );
      default:
        return null;
    }
  };

  return getTableComponent();
};

export default Events;
